import { useState } from 'react'
import { useCategories, useStudents } from '@/lib/queries'
import type { StudentFilters } from '@/lib/queries'
import { PageHeader, Card, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Pagination } from '@/components/ui/Pagination'
import { Pill, UserStatusPill } from '@/components/ui/Pill'
import { Button } from '@/components/ui/Button'
import { formatDate, formatMoney, formatNumber, initials } from '@/lib/format'
import type { Student } from '@/types/api'
import { SubscriptionDialog } from './SubscriptionDialog'
import shared from '@/features/bookings/BookingsPage.module.css'
import styles from './StudentsPage.module.css'

/**
 * A12 — Students.
 *
 * Search covers name, email, AND phone because a support call gives you
 * whichever one the caller happens to know.
 *
 * *** VERIFICATION STATE IS SHOWN, BECAUSE IT BLOCKS BOOKING ***
 * A student who has not verified both channels cannot book. Without surfacing
 * it, "I can't book anything" has no visible cause on this screen.
 */
export function StudentsPage() {
  const categories = useCategories()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [managing, setManaging] = useState<Student | null>(null)

  const filters: StudentFilters = {
    page,
    page_size: pageSize,
    search: search || undefined,
    status: status || undefined,
    category_id: categoryId || undefined,
  }

  const { data, isLoading, isFetching, error, refetch } = useStudents(filters)
  const hasFilters = Boolean(search || status || categoryId)

  const columns: Array<Column<Student>> = [
    {
      key: 'student',
      header: 'Student',
      render: (row) => (
        <div className={styles.studentCell}>
          <span className={styles.avatar} aria-hidden="true">
            {initials(row.full_name)}
          </span>
          <div className={styles.studentText}>
            <span className={shared.primary}>{row.full_name}</span>
            <span className={shared.secondary}>
              {row.email ?? row.phone ?? '—'}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (row) =>
        row.category_name ? (
          <Pill tone="indigo">{row.category_name}</Pill>
        ) : (
          // A student without a category cannot browse mentors at all — the
          // category lock is the first gate in the booking flow.
          <Pill tone="warning">Not chosen</Pill>
        ),
    },
    {
      key: 'verified',
      header: 'Verified',
      render: (row) => (
        <div className={styles.verifyCell}>
          <Pill tone={row.email_verified ? 'success' : 'neutral'} subtle>
            Email
          </Pill>
          <Pill tone={row.phone_verified ? 'success' : 'neutral'} subtle>
            Phone
          </Pill>
        </div>
      ),
    },
    {
      key: 'bookings',
      header: 'Sessions',
      align: 'right',
      render: (row) => (
        <span className="numeric">{formatNumber(row.booking_count)}</span>
      ),
    },
    {
      key: 'spent',
      header: 'Total spent',
      align: 'right',
      render: (row) => (
        <span className={`${shared.primary} numeric`}>
          {formatMoney(row.total_spent)}
        </span>
      ),
    },
    {
      key: 'joined',
      header: 'Joined',
      secondary: true,
      render: (row) => (
        <span className={shared.secondary}>{formatDate(row.created_at)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Account',
      secondary: true,
      render: (row) => <UserStatusPill status={row.status} />,
    },
    {
      key: 'subscription',
      header: '',
      align: 'right',
      width: '150px',
      // Only meaningful for a subscription tenant, but shown for everyone: a
      // per-session client simply has no plans to assign, and the dialog says
      // so rather than the row hiding an action that should exist.
      render: (row) => (
        <Button size="sm" variant="ghost" onClick={() => setManaging(row)}>
          Subscription
        </Button>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Students"
        description="Everyone registered with your organisation."
      />

      <Card flush>
        <div className={shared.filters}>
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Search name, email, or phone…"
            className={shared.search}
            aria-label="Search students"
          />

          <select
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value)
              setPage(1)
            }}
            className={shared.select}
            aria-label="Filter by category"
          >
            <option value="">Any category</option>
            {(categories.data?.categories ?? []).map((category) => (
              <option key={category.category_id} value={category.category_id}>
                {category.name}
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value)
              setPage(1)
            }}
            className={shared.select}
            aria-label="Filter by account status"
          >
            <option value="">Any status</option>
            <option value="ACTIVE">Active</option>
            <option value="PENDING_VERIFICATION">Pending verification</option>
            <option value="SUSPENDED">Suspended</option>
          </select>

          {hasFilters && (
            <button
              type="button"
              className={shared.clear}
              onClick={() => {
                setSearch('')
                setStatus('')
                setCategoryId('')
                setPage(1)
              }}
            >
              Clear
            </button>
          )}
        </div>

        <Table
          columns={columns}
          rows={data?.students ?? []}
          rowKey={(row) => row.user_id}
          loading={isLoading || isFetching}
          error={error}
          onRetry={() => void refetch()}
          isFiltered={hasFilters}
          emptyMessage="No students have registered yet."
          emptyFilteredMessage="No students match these filters."
        />
      </Card>

      {data?.page && (
        <Pagination
          page={data.page}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      )}

      <SubscriptionDialog
        student={managing}
        onClose={() => setManaging(null)}
      />
    </PageStack>
  )
}
